<template>
	<v-app id="app">
		<bootstrap-loader v-if="showBootstrapTenantSelection" :show-tenant-selector="true" :available-tenants="availableTenants" @onSelectTenant="selectTenant"> </bootstrap-loader>

		<v-container v-else-if="loggedInTenant && loggedInTenant.status !== 'Active'" class="fill-height loading-container" fluid>
			<v-layout align-center justify-center>
				<v-flex xs12 sm6 md4>
					<v-flex class="text-center">
						<img style="height: 43px" src="@/assets/yhrs_logo.png" alt="Your HR System" />

						<v-divider class="mt-3 mb-4"></v-divider>
						<h2 class="mt-4 mb-2">Your account is currently inactive</h2>
						<p>
							It looks like the organisation you're trying to access has been deactivated. If you believe this is an error or need further assistance, please contact your administrator or our support team
							<a href="mailto:support@yourhrsystem.com">support@yourhrsystem.com</a>
						</p>

						<div>
							<v-btn class="primary" @click="logout">Logout</v-btn>
							<v-btn @click="reloadPage">Reload page</v-btn>
						</div>
					</v-flex>
				</v-flex>
			</v-layout>
		</v-container>

		<v-container v-else-if="failedToLoadApp" class="fill-height loading-container" fluid>
			<v-layout align-center justify-center>
				<v-flex xs12 sm8 md6>
					<v-flex class="text-center">
						<img style="height: 43px" src="@/assets/yhrs_logo.png" alt="Your HR System" />

						<h2 class="mt-4 mb-2">An issue has occurred</h2>

						<div>
							<v-btn class="primary" @click="logout">Logout</v-btn>
							<v-btn @click="reloadPage">Reload page</v-btn>
						</div>
						<v-divider class="mt-3 mb-4"></v-divider>
						<p>If the problem persists please contact a member of support here: <br /><a href="mailto:support@yourhrsystem.com">support@yourhrsystem.com</a></p>
					</v-flex>
				</v-flex>
			</v-layout>
		</v-container>

		<v-container v-else-if="tenantRouteAccessDenied" class="fill-height loading-container" fluid>
			<v-layout align-center justify-center>
				<v-flex xs12 sm8 md5>
					<v-flex class="text-center">
						<img style="height: 43px" src="@/assets/yhrs_logo.png" alt="Your HR System" />

						<h2 class="mt-4 mb-2">You do not have access to this organisation</h2>
						<p>
							This link points to an organisation that is not available on your account. If you expected to access it, ask an administrator to check your access.
						</p>

						<div>
							<v-btn class="primary" @click="logout">Logout</v-btn>
							<v-btn @click="reloadPage">Reload page</v-btn>
						</div>
					</v-flex>
				</v-flex>
			</v-layout>
		</v-container>

		<page-loader v-else-if="!isAuthenticated || !isBootstrapped"></page-loader>
		<page-loader v-else-if="isWaitingForRouteTenant"></page-loader>

		<div v-else>
			<tenant-selector
				v-if="availableTenants.length > 1 && showTenantSelection"
				:dialog="availableTenants.length > 1 && showTenantSelection"
				:tenants="availableTenants"
				:allow-close="tenantId != null"
				@closeDialog="showTenantSelection = false"
				@onSelectTenant="selectTenant"
			></tenant-selector>

			<v-navigation-drawer v-model="drawer" app>
				<nav-company-logo :logo-url="loggedInTenant != null ? loggedInTenant.logoUrl : null" />

				<div style="position: relative" class="tenant-selector">
					<v-tooltip v-if="availableTenants.length > 1 && tenantId != null" bottom style="position: absolute; top: 0; right: 0; z-index: 99">
						<v-btn slot="activator" fab flat dark @click="showTenantSelection = true">
							<v-icon>repeat</v-icon>
						</v-btn>
						<span>Change Client</span>
					</v-tooltip>

					<name-card mini v-bind="nameCardData"></name-card>
				</div>

				<v-list dense>
					<v-list-tile :class="$route.path === '/' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('home')">
						<v-list-tile-action>
							<v-icon>home</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>Home</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>
					<v-list-tile @click="selectMenuItem('myProfile')">
						<v-list-tile-action>
							<v-icon>person</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>My Profile</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>
					<v-list-tile v-if="showEmployeesMenuItem" :class="$route.path === '/employees' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('employees')">
						<v-list-tile-action>
							<v-icon>group</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>{{ $term('employee', { plural: true, case: 'title' }) }}</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-tile v-if="$_hasAccessToModule('orgChart')" :class="$route.path === '/orgChart' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('orgChart')">
						<v-list-tile-action>
							<v-icon>mdi-file-tree</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>Org Chart</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-tile v-if="$_hasAccessToModule('checkIns')" :class="$route.path === '/checkIns' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('checkIns')">
						<v-list-tile-action>
							<v-icon>mdi-emoticon-happy-outline</v-icon>
						</v-list-tile-action>

						<v-list-tile-title>Check Ins</v-list-tile-title>
					</v-list-tile>

					<v-list-tile v-if="$_hasAccessToModule('performanceReviews')" :class="$route.path === '/prs' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('performanceReviews')">
						<v-list-tile-action>
							<v-icon>mdi-speedometer</v-icon>
						</v-list-tile-action>

						<v-list-tile-title>Performance Reviews</v-list-tile-title>
					</v-list-tile>

					<v-list-tile v-if="availableReports.length > 0" @click="selectMenuItem('reports')">
						<v-list-tile-action>
							<v-icon>mdi-finance</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>Reports</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-tile v-if="$_hasAccessToModule('compliance')" :class="$route.path === '/hrDocuments/policies' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('hrPolicies')">
						<v-list-tile-action>
							<v-icon>mdi-file-document-outline</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title
								>{{ $term('companyPolicy', { plural: true, case: 'title' }) }}
								<v-icon v-if="unacknowledgedPolicyCount > 0" color="error" small>mdi-alert-circle-outline</v-icon>
							</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-tile v-if="isCertificationsEnabled && $_hasAccessToModule('compliance') && this.$_hasAccessToFeature(this.$FeatureId.POSITION_TEMPLATES_MANAGE, this.$PermissionAction.USE) || ($_hasAccessToFeature($FeatureId.CERTIFICATIONS_MANAGE, $PermissionAction.USE) || loggedInEmployeeIsAdminOrManager)" :class="$route.path === '/certifications' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('certifications')">
						<v-list-tile-action>
							<v-icon>mdi-certificate</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title
								>Certifications
								<v-icon v-if="expiredCertifications.length > 0" color="error" small>mdi-alert-circle-outline</v-icon>
							</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-tile v-if="isFormsEnabled" :class="$route.path === '/myForms' ? 'highlighted-menu-item' : ''" @click="selectMenuItem('myForms')">
						<v-list-tile-action>
							<v-icon>mdi-form-select</v-icon>
						</v-list-tile-action>
						<v-list-tile-content>
							<v-list-tile-title>My Forms</v-list-tile-title>
						</v-list-tile-content>
					</v-list-tile>

					<v-list-group v-if="showConfigureCompanyMenuGroup" no-action prepend-icon="settings">
						<template v-slot:activator>
							<v-list-tile>
								<v-list-tile-title>Manage Company</v-list-tile-title>
							</v-list-tile>
						</template>

						<!-- Business Management Section -->
						<v-list-tile v-if="isAssetRegisterEnabled && $_hasAccessToFeature($FeatureId.ASSET_MANAGER_MANAGE, $PermissionAction.USE)" :class="$route.path === '/assets' ? 'highlighted-menu-item' : ''" :to="{ name: 'assets' }">
							<v-list-tile-content>
								<v-list-tile-title>Assets</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile v-if="isCertificationsEnabled && $_hasAccessToModule('compliance') && $_hasAccessToFeature($FeatureId.CERTIFICATIONS_MANAGE, $PermissionAction.USE)" :class="$route.path === '/certificationSettings' ? 'highlighted-menu-item' : ''" :to="{ name: 'certificationSettings' }">
							<v-list-tile-content>
								<v-list-tile-title>Certification Management</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile
							v-if="$_hasAccessToModule('positionDescriptions') && $_hasAccessToFeature($FeatureId.POSITION_TEMPLATES_MANAGE, $PermissionAction.USE)"
							:class="$route.path === '/positionTemplates' ? 'highlighted-menu-item' : ''"
							:to="{ name: 'positionTemplates' }"
						>
							<v-list-tile-content>
								<v-list-tile-title>{{ $term('positionTemplate', { plural: true, case: 'title' }) }}</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" :class="$route.path === '/companySettings' ? 'highlighted-menu-item' : ''" :to="{ name: 'companySettings' }">
							<v-list-tile-content>
								<v-list-tile-title>Company Settings</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile v-if="isFormsEnabled && loggedInEmployee != null && loggedInEmployee.isAdmin" :class="$route.path === '/company/forms' ? 'highlighted-menu-item' : ''" :to="{ name: 'companyForms' }">
							<v-list-tile-content>
								<v-list-tile-title>Forms Management</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-divider></v-divider>

						<!-- Billing Section -->
						<v-list-tile v-if="isBillingEnabled && loggedInEmployee != null && loggedInEmployee.isAdmin" :class="$route.path === '/billing' ? 'highlighted-menu-item' : ''" :to="{ name: 'billing' }">
							<v-list-tile-content>
								<v-list-tile-title>Billing & Subscriptions</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-divider></v-divider>

						<!-- Technical/System Features -->
						<v-list-tile v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" :class="$route.path === '/features' ? 'highlighted-menu-item' : ''" :to="{ name: 'features' }">
							<v-list-tile-content>
								<v-list-tile-title>Feature Management</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile v-if="loggedInEmployee != null && loggedInEmployee.isAdmin" :class="$route.path === '/permissions' ? 'highlighted-menu-item' : ''" :to="{ name: 'permissions' }">
							<v-list-tile-content>
								<v-list-tile-title>Permission Management</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
					</v-list-group>
				</v-list>
				<!--				<onboarding-completion-button id="sideNavOnboardingBtn"/>-->
			</v-navigation-drawer>
			<v-toolbar color="blue darken-3" dark fixed app>
				<v-toolbar-side-icon @click.stop="drawer = !drawer"></v-toolbar-side-icon>
				<v-toolbar-title>{{ pageTitle }}</v-toolbar-title>
				<v-spacer></v-spacer>

				<!--				<onboarding-completion-button/>-->

				<v-autocomplete
					v-if="loggedInEmployee !== null && loggedInEmployee.isAdmin"
					v-model="selectedSearchItem"
					:loading="loading"
					:items="searchItems"
					:search-input.sync="search"
					item-text="fullName"
					item-value="id"
					clearable
					class="mx-3 hidden-xs-and-down"
					flat
					hide-no-data
					hide-details
					label="Search"
					solo-inverted
					style="max-width: 250px"
					@change="searchClickEmployee"
					@input="afterSearchSelection"
				/>

				<v-chip v-if="showEnvironment" label outline color="yellow">{{ environment }}</v-chip>

				<v-layout v-if="isTenantVaccho" class="ml-2 hidden-sm-and-down" style="flex: 0 1 auto">
					<v-flex shrink align-self-center class="mr-2">
						<img style="width: 32px" class="mt-1" src="@/assets/flags/aboriginal-flag.png" :title="australiaCustodiansAck" />
					</v-flex>
					<v-flex shrink align-self-center class="mr-2">
						<img style="width: 32px" class="mt-1" src="@/assets/flags/torres-strait-islander-flag.png" :title="australiaCustodiansAck" />
					</v-flex>
					<v-flex shrink align-self-center>
						<img style="width: 32px" class="mt-1" src="@/assets/flags/lgbtq-flag.png" />
					</v-flex>
				</v-layout>
				<v-toolbar-items class="hidden-sm-and-down">
					<v-menu offset-y origin="center center" class="elelvation-1" :nudge-bottom="14" transition="scale-transition">
						<v-btn slot="activator" flat>
							<span>Support</span>
							<v-icon>mdi-menu-down</v-icon>
						</v-btn>
						<v-list dense>
							<v-subheader>Get Help</v-subheader>
							<v-list-tile>
								<v-list-tile-action>
									<v-icon>mdi-help-circle-outline</v-icon>
								</v-list-tile-action>
								<v-list-tile-content>
									<a href="https://support.yourhrsystem.com" target="_blank" title="Help Docs">Help Docs</a>
								</v-list-tile-content>
							</v-list-tile>
							<v-divider></v-divider>
							<v-list-tile @click="selectMenuItem('feedback')">
								<v-list-tile-action>
									<v-icon>mdi-message-alert</v-icon>
								</v-list-tile-action>
								<v-list-tile-content>Send us feedback</v-list-tile-content>
							</v-list-tile>
							<v-list-tile @click="selectMenuItem('terms')">
								<v-list-tile-action>
									<v-icon>mdi-file-document-edit-outline</v-icon>
								</v-list-tile-action>
								<v-list-tile-content>Terms of Service</v-list-tile-content>
							</v-list-tile>
							<v-list-tile>
								<v-list-tile-action>
									<v-icon>mdi-shield-account-outline</v-icon>
								</v-list-tile-action>
								<v-list-tile-content>
									<a href="https://www.yourhrsystem.com/privacy-policy" target="_blank" title="Privacy Policy ">Privacy Policy</a>
								</v-list-tile-content>
							</v-list-tile>
						</v-list>
					</v-menu>
				</v-toolbar-items>

				<v-menu offset-y origin="center center" :nudge-bottom="10" transition="scale-transition">
					<v-btn slot="activator" icon large flat>
						<v-avatar size="30px">
							<img v-if="loggedInEmployee !== null" :src="getAvatar(loggedInEmployee.id)" class="avatar-img" />
						</v-avatar>
					</v-btn>

					<v-list class="pa-0">
						<v-list-tile>
							<v-list-tile-content>
								<v-list-tile-sub-title>
									{{ nameCardData.name }}
									<span v-if="nameCardData.jobTitle != null && nameCardData.jobTitle != ''">, {{ nameCardData.jobTitle }}</span>
								</v-list-tile-sub-title>
							</v-list-tile-content>
						</v-list-tile>

						<v-list-tile ripple="ripple" @click="selectMenuItem('myProfile')">
							<v-list-tile-action>
								<v-icon>person</v-icon>
							</v-list-tile-action>
							<v-list-tile-content>
								<v-list-tile-title>My Profile</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile v-if="isFormsEnabled" ripple="ripple" @click="selectMenuItem('myForms')">
							<v-list-tile-action>
								<v-icon>mdi-form-select</v-icon>
							</v-list-tile-action>
							<v-list-tile-content>
								<v-list-tile-title>My Forms</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
						<v-list-tile ripple="ripple" @click="logout">
							<v-list-tile-action>
								<v-icon>power_settings_new</v-icon>
							</v-list-tile-action>
							<v-list-tile-content>
								<v-list-tile-title>Sign Out</v-list-tile-title>
							</v-list-tile-content>
						</v-list-tile>
					</v-list>
				</v-menu>
			</v-toolbar>
			<v-content>
				<v-container fluid>
					<router-view :key="$route.fullPath"></router-view>
				</v-container>
			</v-content>
			<FreshworksHelpBtn class="noPrint" />
			<onboarding-welcome-dialog :has-logo="loggedInTenant !== null && typeof loggedInTenant.logoUrl === 'string'" />
		</div>
	</v-app>
</template>

<script>
	import Vue from 'vue';
	import axios from 'axios';
	import moment from 'moment';
	import { mapState, mapGetters } from 'vuex';
	import { InteractionRequiredAuthError } from '@azure/msal-browser';

	import TenantSelector from '@/components/TenantSelector';
	import BootstrapLoader from '@/components/BootstrapLoader';
	import NameCard from '@/components/NameCard';
	import PageLoader from '@/components/PageLoader';
	import avatarMixin from '@/mixins/avatarMixin';
	import FeatureFlag from '@/enums/featureFlag';
	import OnboardingCompletionButton from '@components/onboarding/OnboardingCompletionButton.vue';
	import NavCompanyLogo from '@components/NavCompanyLogo.vue';
	import OnboardingWelcomeDialog from '@components/onboarding/OnboardingWelcomeDialog.vue';
	import FreshworksHelpBtn from '@/components/common/FreshworksHelpBtn';
	import { getTenantIdFromRoute, tenantIdsMatch } from '@/common/tenantRoutes';

	export default {
		components: {
			OnboardingWelcomeDialog,
			NavCompanyLogo,
			OnboardingCompletionButton,
			TenantSelector,
			BootstrapLoader,
			NameCard,
			PageLoader,
			FreshworksHelpBtn
		},
		data: () => ({
			showTenantSelection: false,
			drawer: null,
			loading: false,
			searchItems: [],
			search: null,
			selectedSearchItem: null,
			failedToLoadApp: false,
			tenantRouteAccessDenied: false,
			isSelectingRouteTenant: false
		}),
		props: {
			source: String
		},
		computed: {
			...mapState({
				pageTitle: state => state.pageTitle,
				dirtyEmployee: state => state.employees.dirty_employee,
				tenantId: state => state.tenantId,
				userLoading: state => state.bootstrapData.user_loading
			}),
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant',
				availableTenants: 'availableTenants',
				isBootstrapped: 'isBootstrapped',
				unacknowledgedPolicyCount: 'hrDocuments/unacknowledgedPolicyCount',
				expiredCertifications: 'certifications/expired',
				availableReports: 'reports/availableReports',
				isAuthenticated: 'auth/isAuthenticated',
				loggedInEmployeeIsAdminOrManager: 'loggedInEmployeeIsAdminOrManager'
			}),
			showBootstrapTenantSelection() {
				return this.isAuthenticated && !this.tenantRouteAccessDenied && !this.routeTenantId && !this.isBootstrapped && this.tenantId == null && this.availableTenants.length > 1;
			},
			routeTenantId() {
				return getTenantIdFromRoute(this.$route);
			},
			isWaitingForRouteTenant() {
				return this.isSelectingRouteTenant || (!!this.routeTenantId && this.tenantId != null && !tenantIdsMatch(this.routeTenantId, this.tenantId));
			},
			showEnvironment() {
				return Vue.prototype.$environment != null && Vue.prototype.$environment != '' && Vue.prototype.$environment != 'PROD';
			},
			environment() {
				return Vue.prototype.$environment;
			},
			isBillingEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Billing);
			},
			isAssetRegisterEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.AssetRegister);
			},
			isCertificationsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Certifications);
			},
			isFormsEnabled() {
				return this.$store.getters['features/isFeatureEnabled'](FeatureFlag.Forms);
			},
			nameCardData() {
				if (this.loggedInEmployee != null && this.loggedInTenant != null) {
					try {
						let positionTitle = '';

						if (this.loggedInEmployee.currentPosition) {
							positionTitle = this.loggedInEmployee.currentPosition.title;
						} else {
							positionTitle = this.loggedInEmployee.positions.length > 0 ? this.loggedInEmployee.positions[0].title : '';
						}

						return {
							jobTitle: positionTitle,
							tenantName: this.loggedInTenant.name,
							name: this.loggedInEmployee.fullName,
							color: '#536DFE',
							dark: true,
							isAdmin: this.loggedInEmployee.isAdmin,
							isLoading: this.userLoading,
							id: this.loggedInEmployee.id,
							companyVision: this.loggedInTenant.companyVision
						};
					} catch (err) {
						return {
							jobTitle: '',
							tenantName: '',
							name: '',
							color: '#536DFE',
							dark: true,
							isLoading: this.userLoading
						};
					}
				} else {
					return {
						jobTitle: '',
						tenantName: '',
						name: '',
						color: '#536DFE',
						dark: true,
						isLoading: this.userLoading
					};
				}
			},
			showEmployeesMenuItem() {
				return this.loggedInEmployee != null && (this.loggedInEmployee.isAdmin || (this.loggedInEmployee.underlings != null && this.loggedInEmployee.underlings.length > 0));
			},
			isTenantVaccho() {
				return this.tenantId === 'bc2c615f-1a05-4ffa-9b70-e07d7a91ba3a';
			},
			australiaCustodiansAck() {
				return 'Your HR System acknowledges the traditional custodians of country throughout Australia and their connections to land, sea and community. We pay our respects to their elders past, present and emerging and extend that respect to all Aboriginal and Torres Strait Islander peoples today.';
			},
			showConfigureCompanyMenuGroup() {
				if (!this.loggedInEmployee) return false;

				return this.loggedInEmployee.isAdmin || this.$_hasAccessToFeature(this.$FeatureId.ASSET_MANAGER_MANAGE, this.$PermissionAction.USE) || this.$_hasAccessToFeature(this.$FeatureId.POSITION_TEMPLATES_MANAGE, this.$PermissionAction.USE) || this.$_hasAccessToFeature(this.$FeatureId.CERTIFICATIONS_MANAGE, this.$PermissionAction.USE);
			}
		},
		watch: {
			search(val) {
				val && val !== this.selectedSearchItem && this.querySelections(val);
			},
			// Triggers when the user is authenticated
			isAuthenticated: {
				immediate: true,
				async handler(isAuthenticated) {
					if (isAuthenticated) {
						this.getLoggedInUser();
					}
				}
			},
			'$route.fullPath'() {
				this.selectRouteTenantFromUrl();
			},
			tenantId() {
				this.selectRouteTenantFromUrl();
			}
		},
		mixins: [avatarMixin],
		created() {
			this.configureAxiosInterceptor();
		},
		methods: {
			configureAxiosInterceptor() {
				const ajax = axios.create({
					baseURL: Vue.prototype.$apiBaseUrl
				});

				ajax.interceptors.request.use(
					async config => {
						if (this.$store.getters['loggedInTenant']) {
							config.headers['Tenant-Id'] = this.loggedInTenant.id;
						}

						// Get the access token from the Vuex store
						let accessToken = this.$store.getters['auth/accessToken'];
						// Check if the access token has expired
						if (accessToken == null || moment(accessToken.expiresOn).isBefore(moment())) {
							console.log('access token expired, acquiring a new one...');
							// The access token has expired, obtain a new one
							accessToken = await this.$store.dispatch('auth/acquireTokenSilent');
						}

						// If an access token exists, set the Authorization header
						if (accessToken) {
							config.headers.Authorization = `Bearer ${accessToken.accessToken}`;

							return config;
						}
					},
					error => {
						return Promise.reject(error);
					}
				);

				ajax.interceptors.response.use(
					response => response,
					error => {
						const originalRequest = error.config;

						if (error && error.response && error.response.status === 401 && !originalRequest._retry) {
							originalRequest._retry = true;

							return new Promise((resolve, reject) => {
								this.$store
									.dispatch('auth/acquireTokenSilent')
									.then(() => {
										const accessToken = this.$store.getters['auth/accessToken'];

										originalRequest.headers.Authorization = `Bearer ${accessToken.accessToken}`;

										resolve(ajax(originalRequest));
									})
									.catch(error => {
										reject(error);
									});
							});
						}

						return Promise.reject(error);
					}
				);

				Vue.axios = Vue.prototype.$http = ajax;
			},
			searchClickEmployee(employeeId) {
				if (employeeId !== undefined) {
					this.search = null;
					this.$router.push(`/employee/${employeeId}`);
				}
			},
			reloadPage() {
				window.location.reload();
			},
			afterSearchSelection() {},
			querySelections(v) {
				this.loading = true;
				// Simulated ajax query
				setTimeout(() => {
					this.searchItems = this.$employees.filter(e => {
						return (e.fullName || '').toLowerCase().indexOf((v || '').toLowerCase()) > -1;
					});
					this.loading = false;
				}, 1);
			},
			logout() {
				this.$store.dispatch('auth/logout');
			},
			selectMenuItem(item) {
				if (this.dirtyEmployee) {
					if (!confirm('You have unsaved changes, do you wish to proceed and lose these unsaved changes?')) {
						return;
					} else {
						this.$store.dispatch('employees/setDirtyEmployee', false);
					}
				}

				switch (item) {
					case 'home':
						this.$router.push('/');
						break;
					case 'positionTemplates':
						this.$router.push('/positionTemplates');
						break;
					case 'employees':
						this.$router.push('/employees');
						break;
					case 'performanceReviews':
						this.$router.push('/prs');
						break;
					case 'settings':
						this.$router.push('/settings');
						break;
					case 'coreValues':
						this.$router.push('/coreValues');
						break;
					case 'orgChart':
						this.$router.push('/orgChart');
						break;
					case 'feedback':
						this.$router.push('/feedback');
						break;
					case 'myProfile':
						this.$router.push(`/employee/${this.loggedInEmployee.id}`);
						break;
					case 'myForms':
						this.$router.push('/myForms');
						break;
					case 'categories':
						this.$router.push('/categories');
						break;
					case 'defaultDiscussionPoints':
						this.$router.push('/defaultDiscussionPoints');
						break;
					case 'checkIns':
						this.$router.push('/checkIns');
						break;
					case 'departments':
						this.$router.push('/departments');
						break;
					case 'hrPolicies':
						this.$router.push('/hrDocuments/policies');
						break;
					case 'meetingLocations':
						this.$router.push('/meetingLocations');
						break;
					case 'performanceReviewTemplates':
						this.$router.push('/prTemplates');
						break;
					case 'checkInTemplates':
						this.$router.push('/checkInTemplates');
						break;
					case 'reports':
						this.$router.push('/reports');
						break;
					case 'terms':
						window.open('https://www.yourhrsystem.com/terms/', '_blank', 'noopener');
						break;
					case 'dailyScrum':
						this.$router.push('/dailyScrum');
						break;
					case 'assets':
						this.$router.push('/assets');
						break;
					case 'certifications':
						this.$router.push('/certifications');
						break;
					case 'certificationSettings':
						this.$router.push('/certificationSettings');
						break;
					default:
						break;
				}
			},
			bootstrapApplication() {
				this.$store.dispatch('getBootstapApplicationData').then(
					response => {},
					error => {}
				);
			},
			async getProductPlans() {
				await this.$store.dispatch('productPlans/get');
			},
			getEmployees() {
				this.$store.dispatch('setBootstrapDataPropIsLoading', 'employees');
				this.$store.dispatch('employees/getEmployees').then(
					response => {
						this.$store.dispatch('setBootstrapDataPropIsLoaded', 'employees');
					},
					error => {}
				);
			},
			async getFeatureFlags() {
				await this.$store.dispatch('features/getFeatureFlags');
			},
			getLoggedInUser() {
				this.$store.dispatch('getLoggedInUser').then(
					response => {
						this.tenantRouteAccessDenied = false;
						const routeTenantId = this.routeTenantId;

						if (response.tenants.length == 0) {
							this.failedToLoadApp = true;
						} else if (routeTenantId) {
							this.selectRouteTenantFromUrl(response);
						} else if (this.availableTenants.length == 1) {
							this.selectTenant({ tenant: this.availableTenants[0], employee: response.loggedInEmployee });
						} else if (response.tenants.every(x => x.status === 'Inactive')) {
							// Inactive tenant
							this.selectTenant({ tenant: response.tenants[0], employee: response.loggedInEmployee });
						} else if (response.tenants.length > 1) {
							var storageData = localStorage.getItem(`yhrs_selectedTenant_${response.userId}`);
							if (storageData !== null) {
								let object = JSON.parse(storageData);
								if (moment().diff(object.timestamp, 'hours') <= 1) {
									const cachedTenant = this.availableTenants.find(tenant => tenantIdsMatch(tenant.id, object.tenant && object.tenant.id));
									if (cachedTenant) {
										this.selectTenant({ tenant: cachedTenant });
									}
								}
							}
						}
					},
					error => {
						// This is our first api call, which may lead to an interaction error.
						// It should redirect, this code just prevents telling the user the app failed to load
						// Need to redo this code
						if (!error instanceof InteractionRequiredAuthError || error.response || error.request || error.message === 'Network Error') {
							this.failedToLoadApp = true;
						}
					}
				);
			},
			async selectRouteTenantFromUrl(user) {
				if (!this.routeTenantId) return false;

				const loggedInUser = user || this.$store.getters['loggedInUser'];
				if (!loggedInUser) return false;

				if (this.tenantId && tenantIdsMatch(this.routeTenantId, this.tenantId)) {
					this.tenantRouteAccessDenied = false;
					return true;
				}

				const routeTenant = loggedInUser.tenants.find(tenant => tenantIdsMatch(tenant.id, this.routeTenantId) && tenant.status === 'Active');
				if (!routeTenant) {
					this.tenantRouteAccessDenied = true;
					return true;
				}

				if (this.isSelectingRouteTenant) return true;

				this.isSelectingRouteTenant = true;
				try {
					await this.selectTenant({ tenant: routeTenant, preferFreshEmployee: true });
				} finally {
					this.isSelectingRouteTenant = false;
				}

				return true;
			},
			async selectTenant({ tenant, employee, viaSelector, preferFreshEmployee }) {
				await this.$store.dispatch('setTenantId', tenant.id);

				this.tenantRouteAccessDenied = false;
				this.showTenantSelection = false;

				const loggedInUser = this.$store.getters['loggedInUser'];

				if (tenant.status !== 'Active') {
					if (loggedInUser) {
						localStorage.removeItem(`yhrs_selectedTenant_${loggedInUser.userId}`);
					}
					return;
				}

				var data = {
					tenant: tenant,
					timestamp: new Date().getTime()
				};

				if (loggedInUser) {
					localStorage.setItem(`yhrs_selectedTenant_${loggedInUser.userId}`, JSON.stringify(data));
				}

				if (employee && !preferFreshEmployee) {
					this.$store.commit('SET_LOGGED_IN_EMPLOYEE', employee);
				} else {
					const foundLoggedInEmployee = await this.$store.dispatch('employees/getLoggedInEmployee', true);
					this.$store.commit('SET_LOGGED_IN_EMPLOYEE', foundLoggedInEmployee);
				}

				if (viaSelector) {
					window.location.replace('/');
				}

				this.bootstrapApplication();
				this.getEmployees();
				this.getFeatureFlags();
				this.getProductPlans();

				await this.$store.dispatch('initialiseAccessibleModules');
			}
		}
	};
</script>
